import * as React from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import {FileObject, getImageByPreview} from 'api/library';
import {CourseObject, getCourseByID} from 'api/course';
import FormContainer from '../../containers/FormContainer';
import useLoading from 'hooks/use-loading';
import useModal from 'hooks/use-modal';
import Row from 'components/Row';
import Column from 'components/Column';
import Button, {ButtonVariant} from 'components/Button';
import InstancePicker, {InstanceType} from 'components/InstancePicker';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {IconTrash} from 'icons';

export interface CourseCardProps {
  id: string;
}

const Container = styled.div`
  padding: 10px 0;
  gap: 10px;
  align-items: center;
  grid-template-columns: max-content 1fr max-content;
  display: grid;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({theme}) => theme.variables.borderColor};
  }
`;

const Preview = styled.img`
  object-fit: cover;
  border-radius: 5px;
  max-width: 132px;
  width: 100%;
  height: 74px;
`;

const Title = styled.span`
  font-weight: 500;
  line-height: 20px;
`;

/**
 * Render course card.
 * @param {CourseCardProps} props
 * @return {React.ReactElement<CourseCardProps>}
 * @constructor
 */
function CourseCard(
    props: CourseCardProps,
): React.ReactElement<CourseCardProps> {
  const formContext = useFormikContext<Partial<CourseObject>>();

  const {
    data, setData,
    render,
    fetch,
  } = useLoading<CourseObject>();

  console.log(props);

  React.useEffect(() => {
    console.log(props.id);
    fetch(() => new Promise(((resolve) => (
      getCourseByID(props.id).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          setData(response);
          resolve(response);
        }
      })
    ))));
  }, [props.id]);

  return render(() => {
    /**
     * The delete event handler.
     */
    function handleDelete(): void {
      formContext.setFieldValue('freeCourses.list', formContext.values.freeCourses?.list ? [
        ...formContext.values.freeCourses?.list.filter((id) => (
          id !== props.id
        )),
      ] : []);
      formContext.submitForm();
    }

    return (
      <Container>
        <Preview
          src={getImageByPreview(data?.preview)}
          alt={data?.title}
        />
        <Title>
          {data?.title}
        </Title>
        <Button
          type="button"
          variant={ButtonVariant.Outline}
          onClick={handleDelete}
        >
          <IconTrash />
        </Button>
      </Container>
    );
  }, () => (
    <span>Курс не найден</span>
  ));
}

/**
 * The free courses form part of settings
 * @return {React.ReactElement}
 * @constructor
 */
export default function FreeCoursesForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<CourseObject>>();
  const coursesModal = useModal();

  /**
   * Render instance picker modal.
   * @return {JSX.Element | undefined}
   */
  function renderInstancePicker(): JSX.Element | undefined {
    /**
     * This blur event handler.
     */
    function handleBlur(): void {
      coursesModal.setModalVisible(false);
    }

    /**
     * This select event handler.
     * @param {FileObject[]} attached
     */
    function handleSelect(attached: FileObject['id'][]): void {
      coursesModal.setModalVisible(false);
      formContext.setFieldValue('freeCourses.list', formContext.values.freeCourses?.list ? [
        ...formContext.values.freeCourses?.list,
        ...attached,
      ] : [...attached]);
      formContext.submitForm();
    }

    return coursesModal.isModalVisible ? (
      <InstancePicker
        type={InstanceType.Course}
        renderHeader="Выберите курс"
        excludeId={formContext.values.id}
        onSelect={handleSelect}
        onBlur={handleBlur}
        multiply
      />
    ) : undefined;
  }

  /**
   * Render content
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render list.
     * @return {JSX.Element}
     */
    function renderList(): JSX.Element {
      return (
        <Column>
          {formContext.values.freeCourses?.list?.map((item, index) => (
            <CourseCard key={index} id={item} />
          ))}
        </Column>
      );
    }

    return (
      <Row>
        <Column>
          <Row>
            {renderList()}
          </Row>
          <Row>
            <Column>
              <Button
                type="button"
                variant={ButtonVariant.Outline}
                onClick={() => (
                  coursesModal.setModalVisible(true)
                )}
                stretched
              >
                Добавить курс
              </Button>
            </Column>
          </Row>
        </Column>
      </Row>
    );
  }

  return (
    <FormContainer
      property="freeCourses"
      label="Курсы в подарок"
      collapse
    >
      {renderInstancePicker()}
      {renderContent()}
    </FormContainer>
  );
}
